/**
 * API service for backend communication
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const api = {
  /**
   * Get list of all skylander names
   */
  async getSkylanderNames() {
    const response = await fetch(`${API_BASE_URL}/api/skylanders`);
    if (!response.ok) {
      throw new Error('Failed to fetch skylander names');
    }
    const data = await response.json();
    return data.skylanders;
  },

  /**
   * Verify that a daily skylander exists for the given date
   */
  async getDailySkylander(date) {
    const response = await fetch(`${API_BASE_URL}/api/daily-skylander?date=${date}`);
    if (!response.ok) {
      throw new Error('Failed to fetch daily skylander');
    }
    return await response.json();
  },

  /**
   * Submit a guess and get results
   */
  async submitGuess(guess, date) {
    const response = await fetch(`${API_BASE_URL}/api/submit-guess`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ guess, date }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to submit guess');
    }
    
    return await response.json();
  },

  // Authentication endpoints
  
  /**
   * Register a new user
   */
  async register(email, password) {
    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }
    
    return await response.json();
  },

  /**
   * Login with email and password
   */
  async login(email, password) {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }
    
    return await response.json();
  },

  /**
   * Get current user information
   */
  async getCurrentUser(token) {
    const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to get user information');
    }
    
    return await response.json();
  },

  // Game session endpoints

  /**
   * Save game progress
   */
  async saveGameProgress(token, progressData) {
    const response = await fetch(`${API_BASE_URL}/api/game/save-progress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(progressData),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to save progress');
    }
    
    return await response.json();
  },

  /**
   * Load game progress for a specific date
   */
  async loadGameProgress(token, date) {
    const response = await fetch(`${API_BASE_URL}/api/game/load-progress/${date}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to load progress');
    }
    
    return await response.json();
  },

  /**
   * Check if game is completed for a specific date
   */
  async checkGameCompletion(token, date) {
    const response = await fetch(`${API_BASE_URL}/api/game/check-completion/${date}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to check completion');
    }
    
    return await response.json();
  },

  /**
   * Migrate localStorage data to backend
   */
  async migrateData(token, gameHistory) {
    const response = await fetch(`${API_BASE_URL}/api/game/migrate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ game_history: gameHistory }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to migrate data');
    }
    
    return await response.json();
  },

  // Stats endpoints

  /**
   * Get user statistics
   */
  async getUserStats(token) {
    const response = await fetch(`${API_BASE_URL}/api/stats/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to get statistics');
    }
    
    return await response.json();
  },

  /**
   * Get game history
   */
  async getGameHistory(token, limit = 50, offset = 0) {
    const response = await fetch(
      `${API_BASE_URL}/api/stats/history?limit=${limit}&offset=${offset}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to get game history');
    }
    
    return await response.json();
  },

  /**
   * Reveal the correct answer for a given date
   * Requires proof of game completion (guesses array)
   */
  async revealAnswer(date, guesses) {
    const response = await fetch(`${API_BASE_URL}/api/reveal-answer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ date, guesses }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to reveal answer');
    }
    
    return await response.json();
  }
};
